import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Fab,
  Tabs,
  Tab,
  Checkbox,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  ButtonGroup
} from '@mui/material';
import {
  Logout,
  ShoppingCart,
  Add,
  Check,
  Close,
  LocalGroceryStore,
  Launch,
  Info,
  ArrowBack,
  Store,
  ShoppingBasket,
  AttachMoney,
  AutoAwesome,
  Search,
  CheckCircle,
  QrCodeScanner,
  CameraAlt,
  Edit,
  Link as LinkIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import { useNavigate } from 'react-router-dom';
import { getAmazonSearchUrl, getAmazonProductUrl, AMAZON_DISCLOSURE, canViewShoppingFeatures } from '../utils/amazonHelper';
import BurtSuggestions from '../components/BurtSuggestions';
import BarcodeScanner from '../components/BarcodeScanner';
import PhotoScanner from '../components/PhotoScanner';
import SmartShoppingImport from '../components/SmartShoppingImport';
import ProductSearch from '../components/ProductSearch';
import { aiEstimatePrice } from '../utils/priceEstimator';

const AMAZON_AFFILIATE_TAG = 'earnechorea0f-20'; // Update after re-registering

function ShoppingListPage() {
  const { currentUser, userProfile, logout } = useAuth();
  const { shoppingList, addShoppingItem, updateShoppingItem, deleteShoppingItem } = useFamily();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0); // 0 = Amazon, 1 = Walmart
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'shopping' (in-store mode)
  const [budget, setBudget] = useState(200);
  const [openBudgetDialog, setOpenBudgetDialog] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    price: 0,
    category: '',
    store: 'amazon', // 'amazon' or 'walmart'
    quantity: 1
  });
  const [priceEstimation, setPriceEstimation] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [openBarcodeScanner, setOpenBarcodeScanner] = useState(false);
  const [openPhotoScanner, setOpenPhotoScanner] = useState(false);
  const [addMethod, setAddMethod] = useState('text'); // 'text', 'barcode', 'photo'
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [productImage, setProductImage] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [openAmazonImport, setOpenAmazonImport] = useState(false);
  const [pendingStores, setPendingStores] = useState({}); // { [itemId]: 'amazon' | 'walmart' }
  const [openBulkApproveDialog, setOpenBulkApproveDialog] = useState(false);
  const [bulkApproveStore, setBulkApproveStore] = useState('amazon');
  const [openProductSearch, setOpenProductSearch] = useState(false);

  const isParent = userProfile?.role === 'parent';

  // Separate lists by store
  // If an approved item doesn't have a store, default it to amazon
  const amazonList = shoppingList.filter(item => 
    (item.store === 'amazon' || !item.store) && item.status === 'approved'
  );
  const walmartList = shoppingList.filter(item => 
    item.store === 'walmart' && item.status === 'approved'
  );
  const pendingItems = shoppingList.filter(item => 
    item.status === 'pending' || (!item.status && !item.completed)
  );

  // Calculate totals
  const amazonTotal = amazonList.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
  const walmartTotal = walmartList.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
  const totalSpending = amazonTotal + walmartTotal;
  const budgetPercentage = Math.min((totalSpending / budget) * 100, 100);

  // Load checked state from localStorage
  const [checkedItems, setCheckedItems] = useState(() => {
    const saved = localStorage.getItem('walmartChecked');
    return saved ? JSON.parse(saved) : {};
  });

  // Save checked state to localStorage
  useEffect(() => {
    localStorage.setItem('walmartChecked', JSON.stringify(checkedItems));
  }, [checkedItems]);

  const handleToggleCheck = (itemId) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleMarkAllPurchased = () => {
    const newChecked = {};
    walmartList.forEach(item => {
      newChecked[item.id] = true;
    });
    setCheckedItems(newChecked);
  };

  const handleClearChecked = () => {
    setCheckedItems({});
  };

  // Calculate checked total for Walmart
  const checkedTotal = walmartList
    .filter(item => checkedItems[item.id])
    .reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleAddItem = async () => {
    try {
      await addShoppingItem({
        ...newItem,
        requestedBy: userProfile?.displayName,
        requestedByUid: currentUser.uid,
        status: isParent ? 'approved' : 'pending',
        checked: false,
        addedAt: new Date().toISOString(),
        productImage: productImage || null,
        addMethod: addMethod
      });
      setOpenDialog(false);
      setNewItem({ name: '', price: 0, category: '', store: 'amazon', quantity: 1 });
      setPriceEstimation(null);
      setProductImage(null);
      setAddMethod('text');
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPriceEstimation(null);
    setNewItem({ name: '', price: 0, category: '', store: 'amazon', quantity: 1 });
    setAddMethod('text');
    setProductImage(null);
  };

  const handleBarcodeScanSuccess = async (productData) => {
    console.log('Barcode scan result:', productData);
    
    // Set the product name from barcode lookup
    const productName = productData.name || `Product ${productData.barcode}`;
    
    // Update item with scanned data
    setNewItem(prev => ({
      ...prev,
      name: productName,
      category: productData.category || prev.category
    }));

    // Automatically estimate price
    if (productData.found) {
      // Product was found in database
      try {
        const estimation = await aiEstimatePrice(productName);
        setPriceEstimation(estimation);
        setNewItem(prev => ({
          ...prev,
          price: estimation.estimatedPrice,
          category: estimation.category,
          store: estimation.recommendedStore.store
        }));
      } catch (error) {
        console.error('Error estimating price:', error);
      }
    } else {
      // Product not found, just set name and let user estimate
      setNewItem(prev => ({
        ...prev,
        name: productName
      }));
    }

    // Close barcode scanner, open add dialog
    setOpenBarcodeScanner(false);
    setOpenDialog(true);
    setAddMethod('barcode');
  };

  const handlePhotoScanSuccess = (scanData) => {
    console.log('Photo scan result:', scanData);
    
    // Store the image URL
    setProductImage(scanData.imageUrl);
    
    // Open add dialog for manual entry with AI assist
    setOpenPhotoScanner(false);
    setOpenDialog(true);
    setAddMethod('photo');
  };


  const handleEstimatePrice = async () => {
    if (!newItem.name) return;
    
    setEstimating(true);
    try {
      const estimation = await aiEstimatePrice(newItem.name);
      setPriceEstimation(estimation);
      
      // Auto-fill the form with AI suggestions
      setNewItem({
        ...newItem,
        price: estimation.estimatedPrice,
        category: estimation.category,
        store: estimation.recommendedStore.store
      });
    } catch (error) {
      console.error('Error estimating price:', error);
    } finally {
      setEstimating(false);
    }
  };

  const handleApproveItem = async (itemId, item) => {
    try {
      // Use per-item store override if set, fallback to item's existing store, then 'amazon'
      const store = pendingStores[itemId] || item.store || 'amazon';
      await updateShoppingItem(itemId, {
        status: 'approved',
        store,
        approvedAt: new Date().toISOString()
      });
      // Clean up pending store selection for this item
      setPendingStores(prev => { const n = {...prev}; delete n[itemId]; return n; });
    } catch (error) {
      console.error('❌ Error approving item:', error);
      alert(`Failed to approve item: ${error.message}`);
    }
  };

  const handleBulkApprove = async (store) => {
    try {
      await Promise.all(
        pendingItems.map(item =>
          updateShoppingItem(item.id, {
            status: 'approved',
            store: pendingStores[item.id] || store, // respect per-item overrides
            approvedAt: new Date().toISOString()
          })
        )
      );
      setPendingStores({});
      setOpenBulkApproveDialog(false);
    } catch (error) {
      console.error('❌ Bulk approve error:', error);
      alert(`Bulk approve failed: ${error.message}`);
    }
  };

  const handleRejectItem = async (itemId) => {
    try {
      await deleteShoppingItem(itemId);
    } catch (error) {
      console.error('Error rejecting item:', error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await deleteShoppingItem(itemId);
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem({
      id: item.id,
      name: item.name || item.item || '',
      price: item.price || 0,
      quantity: item.quantity || 1,
      category: item.category || 'Groceries',
      store: item.store || 'walmart',
      productImage: item.productImage || null
    });
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;
    
    try {
      // Only include fields that have values (not undefined)
      const updates = {
        name: editingItem.name,
        price: editingItem.price || 0,
        quantity: editingItem.quantity || 1,
      };
      
      // Only add optional fields if they exist
      if (editingItem.category) updates.category = editingItem.category;
      if (editingItem.store) updates.store = editingItem.store;
      if (editingItem.productImage) updates.productImage = editingItem.productImage;
      
      await updateShoppingItem(editingItem.id, updates);
      setEditingItem(null);
      setOpenItemDialog(false);
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  const handleCloseItemDialog = () => {
    setOpenItemDialog(false);
    setSelectedItem(null);
    setEditingItem(null);
  };

  const openAmazonCart = () => {
    const itemsWithASIN = amazonList.filter(item => item.asin);
    const itemsWithoutASIN = amazonList.filter(item => !item.asin);

    if (itemsWithASIN.length === 0 && itemsWithoutASIN.length > 0) {
      // No ASINs at all — fall back to search
      const searchTerms = itemsWithoutASIN.map(item => item.name).join(', ');
      window.open(getAmazonSearchUrl(searchTerms), '_blank');
      return;
    }

    // Build multi-add-to-cart URL with affiliate tag
    const params = itemsWithASIN.map((item, i) =>
      `ASIN.${i + 1}=${item.asin}&Quantity.${i + 1}=${item.quantity || 1}`
    );
    params.push(`tag=${AMAZON_AFFILIATE_TAG}`);
    window.open(`https://www.amazon.com/gp/aws/cart/add.html?${params.join('&')}`, '_blank');
  };

  const handleAmazonImport = async (productData) => {
    await addShoppingItem({
      ...productData,
      requestedBy: userProfile?.displayName,
      requestedByUid: currentUser.uid,
      status: isParent ? 'approved' : 'pending',
      addedAt: new Date().toISOString()
    });
    setOpenAmazonImport(false);
  };

  const handleProductSearchSelect = async (product) => {
    await addShoppingItem({
      name: product.name,
      price: product.price || 0,
      category: product.store === 'amazon' ? getAmazonCategory(product.name) : 'Groceries',
      store: product.store,
      quantity: 1,
      requestedBy: userProfile?.displayName,
      requestedByUid: currentUser.uid,
      status: isParent ? 'approved' : 'pending',
      checked: false,
      addedAt: new Date().toISOString(),
      productImage: product.image || null,
      productUrl: product.url || null,
      asin: product.asin || null,
      itemId: product.itemId || null,
      priceSource: 'proxy',
      addMethod: 'search'
    });
    setOpenProductSearch(false);
  };

  function getAmazonCategory(name) {
    // Quick category guess from product name (same logic as priceEstimator)
    const n = (name || '').toLowerCase();
    if (n.includes('chip') || n.includes('snack') || n.includes('cookie')) return 'Snacks';
    if (n.includes('milk') || n.includes('cheese') || n.includes('yogurt')) return 'Dairy';
    if (n.includes('chicken') || n.includes('beef') || n.includes('meat')) return 'Meat';
    if (n.includes('bread') || n.includes('bagel')) return 'Bakery';
    if (n.includes('toy') || n.includes('lego') || n.includes('doll')) return 'Toys & Games';
    if (n.includes('book')) return 'Books';
    if (n.includes('shampoo') || n.includes('soap') || n.includes('toothpaste')) return 'Personal Care';
    if (n.includes('detergent') || n.includes('cleaner') || n.includes('paper towel')) return 'Household';
    return 'General';
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" aria-label="back" onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: 2 }}>
            🛒 Family Shopping Hub
          </Typography>
          <Avatar sx={{ bgcolor: 'secondary.main', mx: 2 }}>
            {userProfile?.displayName?.charAt(0)}
          </Avatar>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header & Budget */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingBasket color="primary" />
            Smart Shopping Lists
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Organize your shopping by store and track your spending
          </Typography>

          {/* Budget Tracker */}
          <Card sx={{ mt: 2, bgcolor: totalSpending > budget ? 'error.light' : 'success.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  💰 Weekly Budget
                </Typography>
                <Button size="small" onClick={() => setOpenBudgetDialog(true)}>
                  Change Budget
                </Button>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={budgetPercentage}
                    sx={{ height: 10, borderRadius: 5 }}
                    color={totalSpending > budget ? 'error' : 'success'}
                  />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  ${totalSpending.toFixed(2)} / ${budget.toFixed(2)}
                </Typography>
              </Box>
              {totalSpending > budget && (
                <Typography variant="caption" color="error.dark" sx={{ mt: 1, display: 'block' }}>
                  ⚠️ Over budget by ${(totalSpending - budget).toFixed(2)}!
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Burt AI Assistant */}
        {isParent && (
          <BurtSuggestions
            currentList={shoppingList}
            onAddItem={async (item) => {
              await addShoppingItem({
                ...item,
                requestedBy: userProfile?.displayName,
                requestedByUid: currentUser.uid,
                status: 'approved',
                addedAt: new Date().toISOString()
              });
            }}
            currentTotal={totalSpending}
            budget={budget}
          />
        )}

        {/* Pending Requests (Parents Only) */}
        {isParent && pendingItems.length > 0 && (
          <Card sx={{ mb: 3, bgcolor: 'warning.light' }}>
            <CardContent>
              {/* Header + bulk actions */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Info color="warning" />
                  Pending Requests ({pendingItems.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<ShoppingCart sx={{ color: '#FF9900' }} />}
                    onClick={() => { setBulkApproveStore('amazon'); setOpenBulkApproveDialog(true); }}
                    sx={{ bgcolor: '#FF9900', '&:hover': { bgcolor: '#E68A00' }, color: '#000' }}
                  >
                    All → Amazon
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Store sx={{ color: '#fff' }} />}
                    onClick={() => { setBulkApproveStore('walmart'); setOpenBulkApproveDialog(true); }}
                    sx={{ bgcolor: '#0071CE', '&:hover': { bgcolor: '#004F9F' } }}
                  >
                    All → Walmart
                  </Button>
                </Box>
              </Box>

              <List disablePadding>
                {pendingItems.map((item) => {
                  const itemStore = pendingStores[item.id] || item.store || 'amazon';
                  return (
                    <ListItem
                      key={item.id}
                      divider
                      alignItems="flex-start"
                      sx={{ flexWrap: 'wrap', py: 1.5 }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'warning.main' }}>
                          <LocalGroceryStore />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={item.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              By: {item.requestedBy || 'Unknown'}
                              {item.price > 0 ? ` • $${(item.price || 0).toFixed(2)}` : ''}
                            </Typography>
                            {/* Per-item store toggle */}
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                              <Chip
                                label="🟧 Amazon"
                                size="small"
                                clickable
                                onClick={(e) => { e.stopPropagation(); setPendingStores(prev => ({...prev, [item.id]: 'amazon'})); }}
                                color={itemStore === 'amazon' ? 'warning' : 'default'}
                                variant={itemStore === 'amazon' ? 'filled' : 'outlined'}
                              />
                              <Chip
                                label="🟦 Walmart"
                                size="small"
                                clickable
                                onClick={(e) => { e.stopPropagation(); setPendingStores(prev => ({...prev, [item.id]: 'walmart'})); }}
                                color={itemStore === 'walmart' ? 'info' : 'default'}
                                variant={itemStore === 'walmart' ? 'filled' : 'outlined'}
                              />
                            </Box>
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: { xs: 7, sm: 0 } }}>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleApproveItem(item.id, item); }}
                          sx={{ color: 'success.main', border: '1px solid', borderColor: 'success.main', minWidth: 48, minHeight: 48 }}
                          aria-label="approve item"
                        >
                          <Check fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleRejectItem(item.id); }}
                          sx={{ color: 'error.main', border: '1px solid', borderColor: 'error.main', minWidth: 48, minHeight: 48 }}
                          aria-label="reject item"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Store Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => {
              setSelectedTab(newValue);
              setViewMode('list');
            }}
            centered
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingCart sx={{ color: '#FF9900' }} />
                  <Box>
                    <Typography variant="body1">Amazon</Typography>
                    <Typography variant="caption">{amazonList.length} items • ${amazonTotal.toFixed(2)}</Typography>
                  </Box>
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Store sx={{ color: '#0071CE' }} />
                  <Box>
                    <Typography variant="body1">Walmart</Typography>
                    <Typography variant="caption">{walmartList.length} items • ${walmartTotal.toFixed(2)}</Typography>
                  </Box>
                </Box>
              }
            />
          </Tabs>
        </Paper>

        {/* Amazon List View */}
        {selectedTab === 0 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingCart sx={{ color: '#FF9900' }} />
                  Amazon Shopping List
                </Typography>
                {isParent && amazonList.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<Launch />}
                    onClick={openAmazonCart}
                    sx={{ bgcolor: '#FF9900', '&:hover': { bgcolor: '#E68A00' } }}
                  >
                    {amazonList.filter(i => i.asin).length === 0
                      ? `Search Amazon (${amazonList.length} items)`
                      : amazonList.filter(i => i.asin).length === amazonList.length
                        ? `Buy ${amazonList.length} Items on Amazon`
                        : `Buy on Amazon (${amazonList.filter(i => i.asin).length} of ${amazonList.length})`
                    }
                  </Button>
                )}
              </Box>

              {/* Amazon Disclosure */}
              {isParent && canViewShoppingFeatures(userProfile) && amazonList.length > 0 && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Info sx={{ color: 'info.dark' }} />
                  <Typography variant="caption" color="info.dark">
                    {AMAZON_DISCLOSURE}
                  </Typography>
                </Box>
              )}

              {amazonList.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <LocalGroceryStore sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No items in Amazon list
                  </Typography>
                </Box>
              ) : (
                <List>
                  {amazonList.map((item) => (
                    <ListItem key={item.id} divider>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#FF9900' }}>
                          <LocalGroceryStore />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={item.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              ${item.price.toFixed(2)} × {item.quantity || 1} = ${(item.price * (item.quantity || 1)).toFixed(2)}
                            </Typography>
                            {item.category && (
                              <Chip label={item.category} size="small" sx={{ mt: 0.5, mr: 1 }} />
                            )}
                            {isParent && canViewShoppingFeatures(userProfile) && (
                              <Button
                                startIcon={<Launch />}
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  if (item.productUrl && item.asin) {
                                    // Direct product link with affiliate tag
                                    window.open(getAmazonProductUrl(item.asin), '_blank');
                                  } else {
                                    window.open(getAmazonSearchUrl(item.name), '_blank');
                                  }
                                }}
                                sx={{ mt: 1 }}
                              >
                                {item.asin ? 'Buy on Amazon' : 'Find on Amazon'}
                              </Button>
                            )}
                          </Box>
                        }
                      />
                      {isParent && (
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleDeleteItem(item.id)}
                            sx={{ color: 'error.main' }}
                          >
                            <Close />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        )}

        {/* Walmart List View */}
        {selectedTab === 1 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Store sx={{ color: '#0071CE' }} />
                  Walmart Shopping List
                </Typography>
                {walmartList.length > 0 && (
                  <ButtonGroup variant="contained">
                    <Button
                      onClick={() => setViewMode(viewMode === 'list' ? 'shopping' : 'list')}
                      startIcon={viewMode === 'list' ? <ShoppingBasket /> : <LocalGroceryStore />}
                      sx={{ bgcolor: '#0071CE', '&:hover': { bgcolor: '#004F9F' } }}
                    >
                      {viewMode === 'list' ? 'Start Shopping' : 'List View'}
                    </Button>
                  </ButtonGroup>
                )}
              </Box>

              {/* In-Store Shopping Mode */}
              {viewMode === 'shopping' && walmartList.length > 0 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    🛒 In-Store Shopping Mode
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body1">
                        Cart Total: <strong>${checkedTotal.toFixed(2)}</strong> / ${walmartTotal.toFixed(2)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(checkedTotal / walmartTotal) * 100}
                        sx={{ mt: 1, height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Box>
                      <Button
                        size="small"
                        startIcon={<CheckCircle />}
                        onClick={handleMarkAllPurchased}
                        sx={{ mr: 1 }}
                      >
                        Mark All
                      </Button>
                      <Button
                        size="small"
                        onClick={handleClearChecked}
                      >
                        Clear All
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}

              {walmartList.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <LocalGroceryStore sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No items in Walmart list
                  </Typography>
                </Box>
              ) : (
                <List>
                  {walmartList.map((item) => (
                    <ListItem
                      key={item.id}
                      divider
                      sx={{
                        bgcolor: viewMode === 'shopping' && checkedItems[item.id] ? 'success.light' : 'transparent',
                        transition: 'background-color 0.3s'
                      }}
                    >
                      {viewMode === 'shopping' && (
                        <Checkbox
                          checked={checkedItems[item.id] || false}
                          onChange={() => handleToggleCheck(item.id)}
                          sx={{ mr: 2 }}
                        />
                      )}
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#0071CE' }}>
                          <LocalGroceryStore />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={item.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              ${item.price.toFixed(2)} × {item.quantity || 1} = ${(item.price * (item.quantity || 1)).toFixed(2)}
                            </Typography>
                            {item.category && (
                              <Chip label={item.category} size="small" sx={{ mt: 0.5 }} />
                            )}
                            {item.productUrl && item.store === 'walmart' && (
                              <Button
                                startIcon={<Launch />}
                                size="small"
                                variant="outlined"
                                onClick={(e) => { e.stopPropagation(); window.open(item.productUrl, '_blank'); }}
                                sx={{ mt: 1, ml: item.category ? 1 : 0 }}
                              >
                                View on Walmart.com
                              </Button>
                            )}
                          </Box>
                        }
                      />
                      {isParent && viewMode !== 'shopping' && (
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleDeleteItem(item.id)}
                            sx={{ color: 'error.main' }}
                          >
                            <Close />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add Item Options - Mini FABs */}
        {showAddOptions && (
          <Box sx={{ position: 'fixed', bottom: 90, right: 16, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Fab
              size="small"
              color="primary"
              aria-label="search products"
              onClick={() => {
                setShowAddOptions(false);
                setOpenProductSearch(true);
              }}
              sx={{ boxShadow: 3 }}
            >
              <Search />
            </Fab>
            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              Search
            </Typography>

            <Fab
              size="small"
              color="secondary"
              aria-label="scan barcode"
              onClick={() => {
                setShowAddOptions(false);
                setOpenBarcodeScanner(true);
              }}
              sx={{ boxShadow: 3, mt: 1 }}
            >
              <QrCodeScanner />
            </Fab>
            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              Scan
            </Typography>
            
            <Fab
              size="small"
              color="info"
              aria-label="take photo"
              onClick={() => {
                setShowAddOptions(false);
                setOpenPhotoScanner(true);
              }}
              sx={{ boxShadow: 3, mt: 1 }}
            >
              <CameraAlt />
            </Fab>
            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              Photo
            </Typography>
            
            {isParent && (
              <>
                <Fab
                  size="small"
                  color="warning"
                  aria-label="import from amazon"
                  onClick={() => {
                    setShowAddOptions(false);
                    setOpenAmazonImport(true);
                  }}
                  sx={{ boxShadow: 3, mt: 1 }}
                >
                  <LinkIcon />
                </Fab>
                <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  Amazon
                </Typography>
              </>
            )}
            
            <Fab
              size="small"
              color="success"
              aria-label="type manually"
              onClick={() => {
                setShowAddOptions(false);
                setNewItem({ ...newItem, store: selectedTab === 0 ? 'amazon' : 'walmart' });
                setOpenDialog(true);
                setAddMethod('text');
              }}
              sx={{ boxShadow: 3, mt: 1 }}
            >
              <Edit />
            </Fab>
            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              Type
            </Typography>
          </Box>
        )}

        {/* Main Add Item FAB */}
        <Fab
          color="primary"
          aria-label="add item"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            transform: showAddOptions ? 'rotate(45deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s'
          }}
          onClick={() => setShowAddOptions(!showAddOptions)}
        >
          <Add />
        </Fab>

        {/* Add Item Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {addMethod === 'photo' && '📸 '}
            {addMethod === 'barcode' && '📱 '}
            {addMethod === 'text' && '✏️ '}
            Add Shopping Item
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              
              {/* Show product image if uploaded */}
              {productImage && addMethod === 'photo' && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img
                    src={productImage}
                    alt="Product"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      border: '2px solid #ccc'
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    🤖 Now let me help you add the details!
                  </Typography>
                </Box>
              )}

              <FormControl fullWidth>
                <InputLabel>Store</InputLabel>
                <Select
                  value={newItem.store}
                  onChange={(e) => setNewItem({ ...newItem, store: e.target.value })}
                >
                  <MenuItem value="amazon">🟧 Amazon</MenuItem>
                  <MenuItem value="walmart">🟦 Walmart</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Item Name"
                value={newItem.name}
                onChange={(e) => {
                  setNewItem({ ...newItem, name: e.target.value });
                  setPriceEstimation(null); // Reset estimation when name changes
                }}
                fullWidth
                required
                helperText="Enter product name (e.g., 'Tide Pods' or 'Organic Milk')"
              />
              
              {/* AI Estimate Button */}
              <Button
                onClick={handleEstimatePrice}
                disabled={!newItem.name || estimating}
                startIcon={estimating ? <AutoAwesome className="spin" /> : <AutoAwesome />}
                variant="contained"
                color="secondary"
                fullWidth
              >
                {estimating ? '🤖 Analyzing...' : '🤖 Get AI Price Estimate'}
              </Button>

              {/* AI Estimation Results */}
              {priceEstimation && (
                <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2, border: '2px solid', borderColor: 'primary.main' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesome color="primary" />
                    Burt's Analysis
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Estimated Price:</strong> ${priceEstimation.estimatedPrice.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Typical Range:</strong> {priceEstimation.priceRange}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Category:</strong> {priceEstimation.category}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Recommended Store:</strong> {priceEstimation.recommendedStore.emoji} {priceEstimation.recommendedStore.store === 'amazon' ? 'Amazon' : 'Walmart'}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 1.5, bgcolor: 'info.light', borderRadius: 1, mb: 1 }}>
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      💡 <span>{priceEstimation.aiAnalysis}</span>
                    </Typography>
                  </Box>

                  <Box sx={{ p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      ⚠️ {priceEstimation.disclaimer}
                    </Typography>
                  </Box>
                </Box>
              )}

              <TextField
                label="Price"
                type="number"
                value={newItem.price}
                onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })}
                fullWidth
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
                helperText={priceEstimation ? "AI suggested price filled in. Adjust if needed." : "Or use AI estimate above"}
              />
              <TextField
                label="Quantity"
                type="number"
                value={newItem.quantity}
                onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                fullWidth
                inputProps={{ min: 1 }}
              />
              {newItem.category && (
                <Chip
                  label={`Category: ${newItem.category}`}
                  onDelete={() => setNewItem({ ...newItem, category: '' })}
                  color="primary"
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleAddItem}
              variant="contained"
              disabled={!newItem.name || newItem.price <= 0}
            >
              Add Item
            </Button>
          </DialogActions>
        </Dialog>

        {/* Budget Dialog */}
        <Dialog open={openBudgetDialog} onClose={() => setOpenBudgetDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Set Weekly Budget</DialogTitle>
          <DialogContent>
            <TextField
              label="Weekly Budget"
              type="number"
              value={budget}
              onChange={(e) => setBudget(parseFloat(e.target.value) || 0)}
              fullWidth
              InputProps={{
                startAdornment: <AttachMoney />
              }}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenBudgetDialog(false)}>Cancel</Button>
            <Button onClick={() => setOpenBudgetDialog(false)} variant="contained">
              Save Budget
            </Button>
          </DialogActions>
        </Dialog>

        {/* Barcode Scanner Dialog */}
        <BarcodeScanner
          open={openBarcodeScanner}
          onClose={() => setOpenBarcodeScanner(false)}
          onScanSuccess={handleBarcodeScanSuccess}
        />

        {/* Photo Scanner Dialog */}
        <PhotoScanner
          open={openPhotoScanner}
          onClose={() => setOpenPhotoScanner(false)}
          onScanSuccess={handlePhotoScanSuccess}
        />

        {/* Item Detail Dialog - View/Edit Shopping Request */}
        <Dialog open={openItemDialog} onClose={handleCloseItemDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingItem ? '✏️ Edit Shopping Request' : '🛍️ Shopping Request Details'}
          </DialogTitle>
          <DialogContent>
            {selectedItem && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {/* Product Image */}
                {(editingItem?.productImage || selectedItem.productImage) && (
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <img
                      src={editingItem?.productImage || selectedItem.productImage}
                      alt={editingItem?.name || selectedItem.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: '8px',
                        border: '2px solid #ccc'
                      }}
                    />
                  </Box>
                )}

                {editingItem ? (
                  // Edit Mode
                  <>
                    <FormControl fullWidth>
                      <InputLabel>Store</InputLabel>
                      <Select
                        value={editingItem.store}
                        onChange={(e) => setEditingItem({ ...editingItem, store: e.target.value })}
                      >
                        <MenuItem value="amazon">🟧 Amazon</MenuItem>
                        <MenuItem value="walmart">🟦 Walmart</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Item Name"
                      value={editingItem.name}
                      onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                      fullWidth
                      required
                    />
                    <TextField
                      label="Price"
                      type="number"
                      value={editingItem.price}
                      onChange={(e) => setEditingItem({ ...editingItem, price: parseFloat(e.target.value) || 0 })}
                      fullWidth
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                      }}
                    />
                    <TextField
                      label="Quantity"
                      type="number"
                      value={editingItem.quantity}
                      onChange={(e) => setEditingItem({ ...editingItem, quantity: parseInt(e.target.value) || 1 })}
                      fullWidth
                      inputProps={{ min: 1 }}
                    />
                    <TextField
                      label="Category (Optional)"
                      value={editingItem.category}
                      onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                      fullWidth
                    />
                  </>
                ) : (
                  // View Mode
                  <>
                    <Box>
                      <Typography variant="overline" color="text.secondary">Product Name</Typography>
                      <Typography variant="h6">{selectedItem.name}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="overline" color="text.secondary">Price</Typography>
                        <Typography variant="h6">${selectedItem.price ? selectedItem.price.toFixed(2) : '0.00'}</Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="overline" color="text.secondary">Quantity</Typography>
                        <Typography variant="h6">{selectedItem.quantity || 1}</Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="overline" color="text.secondary">Total</Typography>
                        <Typography variant="h6" color="primary">
                          ${((selectedItem.price || 0) * (selectedItem.quantity || 1)).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="overline" color="text.secondary">Store</Typography>
                      <Typography variant="body1">
                        {selectedItem.store === 'amazon' ? '🟧 Amazon' : '🟦 Walmart'}
                      </Typography>
                    </Box>
                    {selectedItem.category && (
                      <Box>
                        <Typography variant="overline" color="text.secondary">Category</Typography>
                        <Chip label={selectedItem.category} color="primary" />
                      </Box>
                    )}
                    <Box>
                      <Typography variant="overline" color="text.secondary">Requested By</Typography>
                      <Typography variant="body1">{selectedItem.requestedBy}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="overline" color="text.secondary">Requested On</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(selectedItem.addedAt).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                    {selectedItem.addMethod && (
                      <Box>
                        <Typography variant="overline" color="text.secondary">Added Via</Typography>
                        <Chip 
                          label={
                            selectedItem.addMethod === 'barcode' ? '📱 Barcode Scan' :
                            selectedItem.addMethod === 'photo' ? '📸 Photo Upload' :
                            '✏️ Manual Entry'
                          }
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {editingItem ? (
              <>
                <Button onClick={handleCancelEdit}>Cancel</Button>
                <Button onClick={handleUpdateItem} variant="contained" color="primary">
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button onClick={handleCloseItemDialog}>Close</Button>
                {selectedItem && selectedItem.status === 'pending' && (
                  <>
                    {!isParent && currentUser.uid === selectedItem.requestedByUid && (
                      <Button
                        onClick={() => handleEditItem(selectedItem)}
                        variant="outlined"
                        startIcon={<Edit />}
                      >
                        Edit Request
                      </Button>
                    )}
                    {isParent && (
                      <Button
                        onClick={() => handleEditItem(selectedItem)}
                        variant="outlined"
                        startIcon={<Edit />}
                      >
                        Edit
                      </Button>
                    )}
                    {isParent && (
                      <>
                        <Button
                          onClick={() => {
                            handleRejectItem(selectedItem.id);
                            handleCloseItemDialog();
                          }}
                          color="error"
                          variant="outlined"
                        >
                          Reject
                        </Button>
                        <Button
                          onClick={() => {
                            handleApproveItem(selectedItem.id, selectedItem);
                            handleCloseItemDialog();
                          }}
                          color="success"
                          variant="contained"
                        >
                          Approve
                        </Button>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Amazon Import Dialog */}
        <SmartShoppingImport
          open={openAmazonImport}
          onClose={() => setOpenAmazonImport(false)}
          onImport={handleAmazonImport}
        />

        {/* Product Search Dialog */}
        <Dialog
          open={openProductSearch}
          onClose={() => setOpenProductSearch(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Search color="primary" /> Search Products
            </Box>
          </DialogTitle>
          <DialogContent>
            <ProductSearch onSelectProduct={handleProductSearchSelect} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenProductSearch(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Approve Dialog */}
        <Dialog open={openBulkApproveDialog} onClose={() => setOpenBulkApproveDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle>
            {bulkApproveStore === 'amazon' ? '🟧 Approve All → Amazon' : '🟦 Approve All → Walmart'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Approve all {pendingItems.length} pending item{pendingItems.length !== 1 ? 's' : ''} and send to:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                fullWidth
                variant={bulkApproveStore === 'amazon' ? 'contained' : 'outlined'}
                onClick={() => setBulkApproveStore('amazon')}
                startIcon={<ShoppingCart />}
                sx={bulkApproveStore === 'amazon' ? { bgcolor: '#FF9900', color: '#000', '&:hover': { bgcolor: '#E68A00' } } : {}}
              >
                Amazon
              </Button>
              <Button
                fullWidth
                variant={bulkApproveStore === 'walmart' ? 'contained' : 'outlined'}
                onClick={() => setBulkApproveStore('walmart')}
                startIcon={<Store />}
                sx={bulkApproveStore === 'walmart' ? { bgcolor: '#0071CE', '&:hover': { bgcolor: '#004F9F' } } : {}}
              >
                Walmart
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Note: Items where you already selected a store individually will keep their individual selection.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenBulkApproveDialog(false)}>Cancel</Button>
            <Button
              onClick={() => handleBulkApprove(bulkApproveStore)}
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
            >
              Approve All ({pendingItems.length})
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default ShoppingListPage;
